import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthBrand, AuthSide, IconEye, IconWarn, SsoRow } from '../components/auth/AuthBits';
import { googleAuth, login } from '../api';
import { AFTER_AUTH_REDIRECT, GOOGLE_CLIENT_ID } from '../config';
import '../styles/auth.css';
import Seo from '../components/Seo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function LoginPage() {
  

  const [v, setV] = useState({ email: '', password: '' });
  const [err, setErr] = useState({});
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('Signing in…');

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        setBusy(true);
        setBusyLabel('Signing you in…');
        window.location.assign(AFTER_AUTH_REDIRECT);
        return;
      }
      const started = Number(sessionStorage.getItem('tg_google_busy') || 0);
      if (started && Date.now() - started < 60000) {
        setBusy(true);
        setBusyLabel('Signing you in…');
        const t = window.setTimeout(() => {
          setBusy(false);
          try { sessionStorage.removeItem('tg_google_busy'); } catch { /* private mode */ }
          setErr((s) => ({ ...s, form: s.form || 'Google sign-in did not finish. Please try again.' }));
        }, 12000);
        return () => window.clearTimeout(t);
      }
    } catch { /* private mode */ }
  }, []);

  const check = (name, val) => {
    if (name === 'email') {
      if (!val.trim()) return 'Enter your work email.';
      if (!EMAIL_RE.test(val.trim())) return 'That email address looks incomplete.';
    }
    if (name === 'password' && !val) return 'Enter your password.';
    return '';
  };

  const set = (name) => (e) => {
    const val = e.target.value;
    setV((s) => ({ ...s, [name]: val }));
    if (err[name]) setErr((s) => ({ ...s, [name]: check(name, val) }));
  };
  /* validate on blur, not on every keystroke */
  const blur = (name) => (e) => setErr((s) => ({ ...s, [name]: check(name, e.target.value) }));

  const submit = (e) => {
    e.preventDefault();
    const next = { email: check('email', v.email), password: check('password', v.password) };
    setErr(next);
    const bad = Object.keys(next).find((k) => next[k]);
    if (bad) {
      document.getElementById(bad)?.focus();
      return;
    }
    setBusy(true);
    setErr((s) => ({ ...s, form: '' }));
    login({ email: v.email, password: v.password })
      .then((r) => {
        /* Same rule as the host's Login.js: main_admin goes to the admin
           console, everyone else to the dashboard. Full navigation rather than
           client-side routing, because the app lives outside this module. */
        const role = r?.data?.user?.role;
        window.location.assign(role === 'main_admin' ? '/main-admin' : AFTER_AUTH_REDIRECT);
      })
      .catch((e) => {
        setErr((s) => ({
          ...s,
          form: e?.status === 401 ? 'That email and password don’t match.' : e.message,
        }));
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <Seo title="Sign In | TeamGrid™" description="Sign in to your TeamGrid workspace. Your summaries, flags and answers are already waiting, read from work patterns rather than screenshots." />
    <div className="ax">
      <div className="ax-form">
        <div className="ax-inner">
          <AuthBrand />

          <>

              <h1 className="ax-h1">Welcome back</h1>
              <p className="ax-sub">Sign in to your workspace. New here? <Link to="/register">Start a free trial</Link>.</p>

              <SsoRow
                verb="Continue"
                disabled={busy}
                onError={(m) => {
                  setBusy(false);
                  setErr((s2) => ({ ...s2, form: m }));
                }}
                onCredential={(idToken) => {
                  if (!idToken) return undefined;
                  setBusy(true);
                  setBusyLabel('Signing you in…');
                  setErr((s2) => ({ ...s2, form: '' }));
                  /* Same endpoint and payload as the host's AuthContext.googleLogin. */
                  return googleAuth({ idToken, isSignup: false })
                    .then((r) => {
                      const role = r?.data?.user?.role;
                      window.location.assign(role === 'main_admin' ? '/main-admin' : AFTER_AUTH_REDIRECT);
                    })
                    .catch((e2) => {
                      setBusy(false);
                      try { sessionStorage.removeItem('tg_google_busy'); } catch { /* private mode */ }
                      setErr((s2) => ({
                        ...s2,
                        form: e2?.status === 404 || /not found/i.test(e2?.message || '')
                          ? 'No TeamGrid account uses that Google address. Create a workspace first.'
                          : e2?.message || 'Google sign-in failed. Please try again.',
                      }));
                    });
                }}
              />
              {GOOGLE_CLIENT_ID && <div className="ax-or">OR SIGN IN WITH EMAIL</div>}

              <form onSubmit={submit} noValidate>
                <div className={`ax-field${err.email ? ' bad' : ''}`}>
                  <div className="ax-lab"><label htmlFor="email">Work email</label></div>
                  <div className="ax-in">
                    <input
                      id="email" name="email" type="email" inputMode="email"
                      autoComplete="username" autoCapitalize="none" spellCheck="false"
                      placeholder="you@company.com" value={v.email}
                      onChange={set('email')} onBlur={blur('email')}
                      aria-invalid={!!err.email} aria-describedby={err.email ? 'email-err' : undefined}
                    />
                  </div>
                  {err.email && <p className="ax-err" id="email-err" role="alert"><IconWarn />{err.email}</p>}
                </div>

                <div className={`ax-field${err.password ? ' bad' : ''}`}>
                  <div className="ax-lab">
                    <label htmlFor="password">Password</label>
                    <Link to="/forgot-password">Forgot password?</Link>
                  </div>
                  <div className="ax-in has-btn">
                    <input
                      id="password" name="password" type={show ? 'text' : 'password'}
                      autoComplete="current-password" placeholder="••••••••••" value={v.password}
                      onChange={set('password')} onBlur={blur('password')}
                      onKeyUp={(e) => setCaps(e.getModifierState?.('CapsLock') || false)}
                      aria-invalid={!!err.password} aria-describedby={err.password ? 'pw-err' : undefined}
                    />
                    <button type="button" className="ax-eye" onClick={() => setShow((s) => !s)}
                      aria-label={show ? 'Hide password' : 'Show password'} aria-pressed={show}>
                      <IconEye off={show} />
                    </button>
                  </div>
                  {caps && <p className="ax-caps">Caps Lock is on.</p>}
                  {err.password && <p className="ax-err" id="pw-err" role="alert"><IconWarn />{err.password}</p>}
                </div>

                {err.form && <p className="ax-err" role="alert" style={{ marginTop: '16px' }}><IconWarn />{err.form}</p>}

                <button className="ax-submit" type="submit" disabled={busy}>
                  {busy && <span className="ax-spin" />}
                  {busy ? busyLabel : 'Sign in'}
                </button>
              </form>

              <p className="ax-switch">Don&apos;t have a workspace? <Link to="/register">Start your free trial</Link></p>
              <div className="ax-badges"><span>SOC 2</span><span>NO SCREENSHOTS</span><span>GDPR READY</span></div>
          </>
        </div>
      </div>

      <AuthSide
        eyebrow="WELCOME BACK"
        title={<>Your company&apos;s week, <em>already written up</em>.</>}
        points={[
          { b: 'Nothing to catch up on.', t: 'Summaries and flags were waiting before you logged in.' },
          { b: 'Ask in plain English.', t: 'Answers come back with the evidence attached.' },
          { b: 'Patterns, not people.', t: 'No screenshots, no keystrokes. Ever.' },
        ]}
        quote="I open TeamGrid before standup and already know what standup was going to tell me."
        who="Priya Nair" role="COO, Arclight" initials="PN"
        stats={[{ v: '10,000+', l: 'computers connected' }, { v: '4s', l: 'average answer time' }]}
      />
    </div>
  </>
  );
}
